/**
 * SPA route-change accessibility — TASK 7.3 Step 2.
 *
 * Extracted from the mock's `_focusH1()` (L883) and `_afterNav()` (L882):
 *
 *   _focusH1(): document.querySelector('[data-h1]').focus({preventScroll:true})
 *               + #main.scrollTop = 0 + window.scrollTo(0,0)
 *   _afterNav(): _focusH1(); setState({live:'Navigated'})
 *
 * Two deliberate deltas from the mock, both dev-plan TASK 7.3 Step 2:
 *
 *   1. The mock announced the constant string "Navigated" for every route.
 *      The plan requires the PAGE TITLE change be announced, so the announcer
 *      reads the new `<h1>`'s own text.
 *   2. The mock deferred the focus call by `setTimeout(…, 30)` — a workaround
 *      for the dc-runtime re-rendering asynchronously after `setState`. This
 *      shell renders synchronously, so the focus move is synchronous too. No
 *      timer means no window in which focus sits on a detached node.
 *
 * And one addition the mock had no equivalent for: {@link beforeRender}. The
 * mock replaced `main`'s subtree while focus could still be inside it, which
 * strands focus on an unmounted element (the browser then resets it to
 * `<body>` with no announcement). Calling `beforeRender` first parks focus on
 * `<body>` deliberately, so the only focus move a user perceives is the one
 * onto the new heading.
 *
 * @module
 */

/**
 * How close (px) to the saved offset counts as "arrived" — a couple of pixels
 * of sub-pixel rounding must not keep the restore loop spinning.
 */
export const SCROLL_TOLERANCE_PX = 2;

/**
 * Frames of NO progress (the document stopped growing and we still cannot reach
 * the offset) after which the restore gives up. Must exceed a slow cold fetch —
 * a paginated list grows one server page at a time, and each page can be a few
 * hundred ms apart — so a stall shorter than that would abandon a list that is
 * still filling in. ~48 frames (~0.8 s at 60 fps) clears a slow page.
 */
export const SCROLL_RESTORE_STALL_FRAMES = 48;

/**
 * Absolute backstop on a single restore, however much progress is being made —
 * a runaway guard so a page whose height never settles (streaming content,
 * lazy media) cannot keep the loop alive indefinitely. ~300 frames (~5 s).
 */
export const SCROLL_RESTORE_MAX_FRAMES = 300;

/**
 * @typedef {object} RouteAnnouncerHandle
 * @property {(container: Element) => void} beforeRender park focus off any
 *   node inside `container` that is about to be replaced
 * @property {(options: { container: Element, scrollTarget?: Element|null }) => string|null}
 *   afterNavigate focus the new `<h1 data-h1>`, reset scroll, announce its
 *   text. Returns the announced string, or null when no heading was found.
 */

/**
 * @param {{
 *   announce: (message: string) => void,
 *   doc?: Document,
 *   win?: Window,
 * }} options
 * @returns {RouteAnnouncerHandle}
 */
export function createRouteAnnouncer(options) {
  const { announce, doc = document, win = window } = options;

  /*
   * SCROLL RESTORATION (feature #2 — "back to the prev screen and its state").
   *
   * Every navigation used to slam the scroll to the top. Now the OUTGOING
   * route's scroll is remembered (keyed by its `location.hash`) as it leaves,
   * and the INCOMING route's remembered position is restored when it returns —
   * so tapping into an album and pressing Back drops you back exactly where you
   * were, not at the top. A route never visited (a fresh forward navigation)
   * has no saved position and correctly lands at the top.
   *
   * `beforeRender` cannot read the outgoing hash — by the time it runs the hash
   * has already changed — so the key of the route on screen is tracked here and
   * updated by `afterNavigate`.
   */
  /** @type {Map<string, { target: number, y: number }>} */
  const scrollByKey = new Map();
  /** @type {string|null} the hash of the route currently on screen. */
  let currentKey = null;

  /**
   * Cancels the window-scroll restoration currently in flight, if any. A new
   * navigation supersedes an unfinished restore, and a deliberate user scroll
   * aborts it — the restore must never fight the user. Null when idle.
   *
   * @type {(() => void)|null}
   */
  let cancelRestore = null;

  function stopRestore() {
    if (cancelRestore !== null) {
      cancelRestore();
      cancelRestore = null;
    }
  }

  /**
   * Pulls the WINDOW back to `targetY` and keeps pulling across animation
   * frames until it sticks — the fix for "Back forgets where I was" (#2).
   *
   * The document is the app's real scroller (the header and nav are fixed), and
   * a route we return to RE-FETCHES its content, so at the instant the view
   * mounts the page is only a heading tall and a single `scrollTo` clamps to ~0.
   * By re-applying the offset each frame until `scrollY` actually reaches it
   * (then holds for two frames), the position lands the moment the async
   * content has grown tall enough — however many hundred ms that takes.
   *
   * Progress-aware, so it also restores DEEP positions on paginated lists: a
   * list re-mounts showing only its first window, but each `scrollTo` pulls the
   * document's load-more sentinel into view, which auto-loads the next page,
   * which grows the document, which lets the next frame scroll further — the
   * loop rides that cascade down to the saved offset. It keeps going while the
   * page is still getting closer to the target, and only stands down once
   * progress has STALLED for {@link SCROLL_RESTORE_STALL_FRAMES} (the list ran
   * out, or the target is genuinely unreachable).
   *
   * Best-effort and self-limiting: no `requestAnimationFrame` (older engines,
   * jsdom without a visual loop) means the single synchronous apply in
   * {@link afterNavigate} stands, exactly as before; {@link SCROLL_RESTORE_MAX_FRAMES}
   * is an absolute backstop; and the first wheel/touch/key/pointer input from
   * the user cancels it outright — the restore never fights the user.
   *
   * @param {number} targetY the saved `window.scrollY` to return to
   */
  function restoreWindowScroll(targetY) {
    stopRestore();
    if (targetY <= 0) return; // a fresh route (or the top) needs no chase
    if (typeof win.requestAnimationFrame !== 'function') return;
    if (typeof win.scrollTo !== 'function') return;

    let frames = 0;
    let settledFrames = 0;
    let stalledFrames = 0;
    let bestReached = -1; // furthest scrollY seen — rising means progress
    let cancelled = false;

    const onUserScroll = () => {
      cancelled = true;
    };
    const events = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
    const canListen =
      typeof win.addEventListener === 'function' && typeof win.removeEventListener === 'function';
    if (canListen) {
      for (const type of events) win.addEventListener(type, onUserScroll, { passive: true });
    }
    const cleanup = () => {
      if (canListen) for (const type of events) win.removeEventListener(type, onUserScroll);
      cancelRestore = null;
    };

    const step = () => {
      if (cancelled) {
        cleanup();
        return;
      }
      win.scrollTo(0, targetY);
      const current = typeof win.scrollY === 'number' ? win.scrollY : 0;

      if (Math.abs(current - targetY) <= SCROLL_TOLERANCE_PX) {
        // Reached it — confirm it holds for one more frame (content may still be
        // settling) before standing down.
        settledFrames += 1;
        if (settledFrames >= 2) {
          cleanup();
          return;
        }
      } else {
        settledFrames = 0;
        // Still short of the target. As long as the reachable offset keeps
        // rising, the document is still growing toward it — keep waiting. Once
        // it stops rising, the content has run out and the target is out of
        // reach; give up after a grace window that outlasts a slow page fetch.
        if (current > bestReached + SCROLL_TOLERANCE_PX) {
          bestReached = current;
          stalledFrames = 0;
        } else {
          stalledFrames += 1;
          if (stalledFrames >= SCROLL_RESTORE_STALL_FRAMES) {
            cleanup();
            return;
          }
        }
      }
      frames += 1;
      if (frames >= SCROLL_RESTORE_MAX_FRAMES) {
        cleanup();
        return;
      }
      win.requestAnimationFrame(step);
    };

    cancelRestore = () => {
      cancelled = true;
      cleanup();
    };
    win.requestAnimationFrame(step);
  }

  /**
   * @param {Element|null} scrollTarget
   * @returns {{ target: number, y: number }}
   */
  function readScroll(scrollTarget) {
    return {
      target: scrollTarget === null ? 0 : scrollTarget.scrollTop,
      y: typeof win.scrollY === 'number' ? win.scrollY : 0,
    };
  }

  /**
   * @param {Element|null} scrollTarget
   * @param {{ target: number, y: number }} pos
   */
  function applyScroll(scrollTarget, pos) {
    try {
      if (scrollTarget !== null) scrollTarget.scrollTop = pos.target;
      if (typeof win.scrollTo === 'function') win.scrollTo(0, pos.y);
    } catch {
      /* non-fatal: scrolling is cosmetic, focus + announcement are not */
    }
  }

  /** @param {Element} container */
  function beforeRender(container) {
    // A navigation is starting: abandon any restore still chasing the previous
    // route's offset before we overwrite the target.
    stopRestore();
    // Remember where the route we are LEAVING was scrolled to, before its DOM
    // is torn down (#2).
    if (currentKey !== null) scrollByKey.set(currentKey, readScroll(container));

    const active = doc.activeElement;
    if (active === null || active === doc.body) return;
    if (!container.contains(active)) return;
    if (typeof (/** @type {HTMLElement} */ (active).blur) === 'function') {
      /** @type {HTMLElement} */ (active).blur();
    }
    // Explicitly park on <body>: blur() alone leaves activeElement null in
    // some engines, and a null activeElement makes the next focus move
    // unannounced by some screen readers.
    if (typeof doc.body.focus === 'function') doc.body.focus({ preventScroll: true });
  }

  /**
   * @param {{ container: Element, scrollTarget?: Element|null }} args
   * @returns {string|null}
   */
  function afterNavigate(args) {
    const { container, scrollTarget = null } = args;
    const heading = /** @type {HTMLElement|null} */ (container.querySelector('[data-h1]'));
    if (heading === null) return null;

    heading.focus({ preventScroll: true });

    // RESTORE this route's remembered scroll (#2). A route we have never left
    // has no entry and lands at the top — the old always-reset behaviour. A
    // host without `scrollTo` (jsdom, some webviews) must not abort the
    // navigation; the focus move above has already happened.
    const key = win.location?.hash ?? '';
    const saved = scrollByKey.get(key) ?? { target: 0, y: 0 };
    applyScroll(scrollTarget, saved);
    // Async / virtualised content grows AFTER this tick, so the synchronous
    // apply above lands short of the target on any route that fetches its rows.
    // Keep pulling the window back to the saved offset until the content is
    // tall enough for it to stick (#2).
    restoreWindowScroll(saved.y);
    currentKey = key;

    const message = (heading.textContent ?? '').trim();
    announce(message === '' ? 'Navigated' : message);
    return message;
  }

  return { beforeRender, afterNavigate };
}
